import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, RefreshCw, Sparkles, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';
import { useAppUpdate } from '@/hooks/useAppUpdate';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

export function UpdateDialog() {
  const {
    hasUpdate,
    isForceUpdate,
    versionInfo,
    currentVersion,
    downloadUpdate,
    dismissUpdate,
    isNativeApp,
    checkForUpdates,
  } = useAppUpdate();

  const [open, setOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (hasUpdate && versionInfo) {
      setOpen(true);
      // Vibrar para notificar
      try {
        Haptics.impact({ style: ImpactStyle.Medium });
      } catch (e) {
        // Haptics no disponible
      }
    }
  }, [hasUpdate, versionInfo]);

  // No mostrar nada si no es app nativa o no hay actualización
  if (!isNativeApp || !hasUpdate || !versionInfo) {
    return null;
  }

  const handleDismiss = () => {
    if (!isForceUpdate) {
      dismissUpdate();
      setOpen(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (e) {
      // Haptics no disponible
    }
    downloadUpdate();
    // El APK se descargará externamente
    setTimeout(() => setIsDownloading(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={isForceUpdate ? undefined : setOpen}>
      <DialogContent className="max-w-md" onPointerDownOutside={isForceUpdate ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {isForceUpdate ? (
              <>
                <AlertTriangle className="w-6 h-6 text-orange-500" />
                Actualización Requerida
              </>
            ) : (
              <>
                <Sparkles className="w-6 h-6 text-primary" />
                ¡Nueva Versión Disponible!
              </>
            )}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {isForceUpdate 
              ? 'Se requiere actualizar la aplicación para continuar usándola.'
              : 'Hay una nueva versión de Madelmold disponible con mejoras y correcciones.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Version info */}
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Tu versión</p>
              <p className="font-mono font-semibold">{currentVersion}</p>
            </div>
            <RefreshCw className="w-5 h-5 text-muted-foreground mx-4" />
            <div>
              <p className="text-sm text-muted-foreground">Nueva versión</p>
              <p className="font-mono font-semibold text-primary">{versionInfo.version}</p>
            </div>
          </div>

          {/* Changelog */}
          {versionInfo.changelog && versionInfo.changelog.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Novedades:</p>
              <ul className="space-y-1.5">
                {versionInfo.changelog.map((item, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Release date */}
          <p className="text-xs text-muted-foreground text-center">
            Publicada el {new Date(versionInfo.releaseDate).toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {!isForceUpdate && (
            <Button variant="outline" onClick={handleDismiss} className="w-full sm:w-auto">
              Más tarde
            </Button>
          )}
          <Button
            onClick={handleDownload}
            disabled={isDownloading}
            className="w-full sm:w-auto bg-gradient-magma hover:opacity-90"
          >
            {isDownloading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Descargando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Descargar Actualización
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
